// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The shareable fit card — a 1200×630 PNG rendered on-device.
 *
 * This is the artefact that travels: someone asks "does Qwen3-32B fit on a
 * 3090?" and the answer is an image with the actual line items on it, not a
 * number with no provenance.
 */

import { bytesToGiB, formatBpw, formatContext, formatParams } from './format';
import type { Geometry } from './geometry';
import type { FitResult, KvSummary, WeightSummary } from './vram';

const W = 1200;
const H = 630;

const COLOURS = {
  bg: '#08080f',
  panel: '#12121f',
  border: '#272740',
  text: '#eceaf6',
  dim: '#aeabc6',
  faint: '#7d7a99',
  accent: '#8b7bff',
  weights: '#8b7bff',
  kv: '#4fb0ff',
  reserve: '#6b6b8f',
  good: '#4ade80',
  amber: '#fbbf24',
  danger: '#f87171',
};

export interface CardInput {
  label: string;
  geo: Geometry;
  weights: WeightSummary;
  kv: KvSummary;
  fit: FitResult;
  nCtx: number;
  cacheTypeK: string;
  cacheTypeV: string;
  quantLabel?: string;
  verified: boolean;
}

const VERDICT_TEXT: Record<FitResult['verdict'], string> = {
  fits: 'FITS',
  tight: 'TIGHT',
  no: "DOESN'T FIT",
};

const VERDICT_COLOUR: Record<FitResult['verdict'], string> = {
  fits: COLOURS.good,
  tight: COLOURS.amber,
  no: COLOURS.danger,
};

export function drawReportCard(canvas: HTMLCanvasElement, input: CardInput): void {
  canvas.width = W;
  canvas.height = H;
  const c = canvas.getContext('2d');
  if (!c) return;

  const mono = '"SF Mono", ui-monospace, Menlo, Consolas, monospace';
  const sans = '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Helvetica, Arial, sans-serif';

  // Ground
  c.fillStyle = COLOURS.bg;
  c.fillRect(0, 0, W, H);
  const glow = c.createRadialGradient(W / 2, -80, 40, W / 2, -80, 720);
  glow.addColorStop(0, 'rgba(139,123,255,0.20)');
  glow.addColorStop(1, 'rgba(139,123,255,0)');
  c.fillStyle = glow;
  c.fillRect(0, 0, W, 400);

  // Header
  c.fillStyle = COLOURS.accent;
  c.font = `600 22px ${sans}`;
  c.fillText('GGUFScope', 64, 74);
  // Measure while the bold face is still selected, or the gap is wrong.
  const brandW = c.measureText('GGUFScope').width;
  c.fillStyle = COLOURS.faint;
  c.font = `20px ${sans}`;
  c.fillText('· will this GGUF fit on your GPU?', 64 + brandW + 14, 74);

  // Model name
  c.fillStyle = COLOURS.text;
  c.font = `600 34px ${mono}`;
  c.fillText(truncate(c, input.label, W - 128), 64, 132);

  c.fillStyle = COLOURS.dim;
  c.font = `20px ${mono}`;
  const sub = [
    input.geo.arch,
    `${formatParams(input.weights.params)} params`,
    formatBpw(input.weights.bpw),
    `${input.geo.nLayer} layers`,
  ].join('   ·   ');
  c.fillText(sub, 64, 168);

  // Verdict
  const verdict = input.fit.verdict;
  c.fillStyle = VERDICT_COLOUR[verdict];
  c.font = `700 76px ${sans}`;
  c.fillText(VERDICT_TEXT[verdict], 64, 268);

  const vw = c.measureText(VERDICT_TEXT[verdict]).width;
  c.fillStyle = COLOURS.dim;
  c.font = `26px ${mono}`;
  const headroom =
    input.fit.headroomBytes >= 0
      ? `${bytesToGiB(input.fit.headroomBytes, 1)} to spare`
      : `${bytesToGiB(-input.fit.headroomBytes, 1)} over`;
  c.fillText(`on ${bytesToGiB(input.fit.vramBytes, 0)}  —  ${headroom}`, 64 + vw + 28, 264);

  // Stacked bar
  const barX = 64;
  const barY = 306;
  const barW = W - 128;
  const barH = 46;
  const cap = Math.max(input.fit.vramBytes, input.fit.requiredBytes);

  c.fillStyle = COLOURS.panel;
  roundRect(c, barX, barY, barW, barH, 8);
  c.fill();

  let x = barX;
  const segs: Array<[number, string]> = [
    [input.fit.weightBytesGpu, COLOURS.weights],
    [input.fit.kvBytesGpu, COLOURS.kv],
    [input.fit.reserveBytes, COLOURS.reserve],
  ];
  c.save();
  roundRect(c, barX, barY, barW, barH, 8);
  c.clip();
  for (const [bytes, colour] of segs) {
    const w = (bytes / cap) * barW;
    c.fillStyle = colour;
    c.fillRect(x, barY, w, barH);
    x += w;
  }
  c.restore();

  // The card's memory line
  const limitX = barX + (input.fit.vramBytes / cap) * barW;
  if (limitX < barX + barW - 1) {
    c.strokeStyle = COLOURS.text;
    c.lineWidth = 3;
    c.beginPath();
    c.moveTo(limitX, barY - 8);
    c.lineTo(limitX, barY + barH + 8);
    c.stroke();
  }
  c.strokeStyle = COLOURS.border;
  c.lineWidth = 1;
  roundRect(c, barX, barY, barW, barH, 8);
  c.stroke();

  // Legend
  const legend: Array<[string, string, number]> = [
    ['weights', COLOURS.weights, input.fit.weightBytesGpu],
    [`KV @ ${formatContext(input.kv.paddedCtx)}`, COLOURS.kv, input.fit.kvBytesGpu],
    ['working memory', COLOURS.reserve, input.fit.reserveBytes],
  ];
  let lx = barX;
  c.font = `20px ${mono}`;
  for (const [name, colour, bytes] of legend) {
    c.fillStyle = colour;
    roundRect(c, lx, 380, 14, 14, 3);
    c.fill();
    c.fillStyle = COLOURS.dim;
    const text = `${name}  ${bytesToGiB(bytes, 2)}`;
    c.fillText(text, lx + 24, 393);
    lx += 24 + c.measureText(text).width + 46;
  }

  // Detail rows
  const rows: Array<[string, string]> = [
    ['context', `${formatContext(input.kv.paddedCtx)} tokens, KV cache ${input.cacheTypeK}/${input.cacheTypeV}`],
    [
      'gpu layers',
      input.fit.plan.outputOnGpu
        ? `all ${input.geo.nLayer} + output head`
        : `${input.fit.plan.layersOnGpu} of ${input.geo.nLayer}`,
    ],
    [
      'quant mix',
      input.weights.byType
        .slice(0, 4)
        .map((t) => `${t.name}×${t.count}`)
        .join('  '),
    ],
  ];
  let ry = 450;
  for (const [k, v] of rows) {
    c.fillStyle = COLOURS.faint;
    c.font = `19px ${mono}`;
    c.fillText(k, 64, ry);
    c.fillStyle = COLOURS.text;
    c.fillText(truncate(c, v, W - 340), 240, ry);
    ry += 34;
  }

  // Footer
  c.fillStyle = COLOURS.border;
  c.fillRect(64, H - 76, W - 128, 1);
  c.fillStyle = COLOURS.faint;
  c.font = `18px ${sans}`;
  c.fillText(
    input.verified
      ? 'Read from the file\'s own tensor table — the sum matches its byte size exactly.'
      : 'Read from the file\'s own tensor table.',
    64,
    H - 44,
  );
  c.textAlign = 'right';
  c.fillStyle = COLOURS.accent;
  c.fillText('ggufscope.benrichardson.dev', W - 64, H - 44);
  c.textAlign = 'left';
}

function roundRect(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}

function truncate(c: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (c.measureText(text).width <= maxWidth) return text;
  let s = text;
  while (s.length > 4 && c.measureText(`${s}…`).width > maxWidth) s = s.slice(0, -1);
  return `${s}…`;
}
